import type {
  BindingKind,
  FieldDef,
  FieldType,
  MasterDefinition,
  SportDefinitionDoc,
} from "@/lib/api/types";

/** A blank field to seed a new FieldEditor row. */
export function emptyField(): FieldDef {
  return { key: "", label: "", type: "text", required: false };
}

/** A blank definition draft for the editor. */
export function emptyDefinition(): SportDefinitionDoc {
  return {
    sportCode: "",
    partOf: null,
    units: [],
    activeRoleCodes: [],
    masters: [],
    fixtureTypes: [],
    configs: [],
  };
}

/** Turn a label into a master/field code: "Race day" → "race-day". */
export function toCode(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

/** A blank master of the chosen kind, pre-wired with the sensible defaults an
 * operator would otherwise have to set by hand (a name-derived code rule, and
 * a participant type for party masters). */
export function emptyMaster(kind: BindingKind, label: string): MasterDefinition {
  const binding =
    kind === "participant"
      ? { kind, participantType: "TEAM" as const }
      : kind === "outcome-codes"
        ? { kind, codeType: "" }
        : kind === "name-history" || kind === "media"
          ? { kind, target: "" }
          : { kind };
  return {
    code: toCode(label) || "master",
    label: label.trim() || "Master",
    binding,
    fields: [],
    codeRule: { from: ["name"] },
    relations: [],
  };
}

// Constraint keys reset when a field's type changes (so a leftover min/options
// from a previous type doesn't linger).
const CONSTRAINT_KEYS: (keyof FieldDef)[] = [
  "min",
  "max",
  "step",
  "unit",
  "asPercent",
  "maxLength",
  "pattern",
  "options",
  "multiple",
  "target",
  "participantType",
];

/** Change a field's type, clearing type-specific constraints + seeding required ones. */
export function changeFieldType(field: FieldDef, type: FieldType): FieldDef {
  const next: FieldDef = { key: field.key, label: field.label, type, required: field.required };
  for (const key of CONSTRAINT_KEYS) delete (next as Record<string, unknown>)[key];
  if (type === "enum") next.options = [];
  if (type === "duration") next.unit = "s";
  return next;
}

/** Normalize a draft for submission: drop unfilled fields/units so a half-typed
 * row doesn't reach the backend. Real validation stays server-side (the catalog). */
export function assembleDoc(draft: SportDefinitionDoc): SportDefinitionDoc {
  const cleanFields = (fields: FieldDef[]): FieldDef[] => fields.filter((f) => f.key.trim() !== "");
  // A loaded document carries only the sections somebody set — the API
  // serialises with `exclude_unset`, so a sport that never declared entities has
  // no key at all. Reading it as though it were always there crashed the setup
  // screen on every seeded sport.
  const cleaned = Object.entries(draft.entities ?? {}).map(([target, fields]) => [
    target,
    cleanFields(fields),
  ]);
  // And it stays absent rather than becoming `{}`: opening a screen should not
  // add empty sections to a document nobody edited.
  const entities = cleaned.length > 0 ? Object.fromEntries(cleaned) : undefined;
  const units = (draft.units ?? []).filter((u) => u.unit.trim() !== "");
  const liveState = draft.liveState
    ? {
        fields: cleanFields(draft.liveState.fields),
        periods: Object.fromEntries(
          Object.entries(draft.liveState.periods).map(([unit, p]) => [
            unit,
            { fields: cleanFields(p.fields) },
          ]),
        ),
      }
    : undefined;
  // Drop half-typed masters and the empty sub-objects an editor leaves behind,
  // so a draft in progress never reaches the backend as an invalid config.
  const masters = (draft.masters ?? [])
    .filter((m) => m.code.trim() !== "")
    .map((m) => ({
      ...m,
      code: m.code.trim(),
      label: m.label.trim() || m.code.trim(),
      fields: cleanFields(m.fields ?? []),
      relations: (m.relations ?? []).filter((r) => r.master !== ""),
      codeRule:
        m.codeRule && m.codeRule.from.length > 0 && m.codeRule.from.every(Boolean)
          ? m.codeRule
          : undefined,
      list: m.list && m.list.columns.length > 0 ? m.list : undefined,
    }));
  const masterCodes = new Set(masters.map((m) => m.code));

  // Drop half-typed types the same way masters are dropped: a type with no
  // code or no entrant is something the operator started, not something the
  // backend should be asked to validate.
  const fixtureTypes = (draft.fixtureTypes ?? [])
    .filter((t) => t.code.trim() !== "" && t.entrant?.master)
    .map((t) => ({
      ...t,
      code: t.code.trim(),
      label: t.label.trim() || t.code.trim(),
      entryFields: cleanFields(t.entryFields ?? []),
      resultFields: cleanFields(t.resultFields ?? []),
      format: cleanFields(t.format ?? []),
      composition: t.composition?.master
        ? {
            ...t.composition,
            slots: (t.composition.slots ?? []).map((p) => p.trim()).filter(Boolean),
            fields: cleanFields(t.composition.fields ?? []),
          }
        : undefined,
      rankings: (t.rankings ?? []).filter((r) => r.code.trim() !== ""),
    }));

  return {
    ...draft,
    sportCode: draft.sportCode.trim(),
    fixtureTypes,
    entities,
    units,
    liveState,
    activeRoleCodes: (draft.activeRoleCodes ?? []).map((c) => c.trim()).filter(Boolean),
    masters,
    // Only send a fixture parent that still exists — a master may have been
    // renamed or removed after it was chosen.
    fixtureParent:
      draft.fixtureParent && masterCodes.has(draft.fixtureParent) ? draft.fixtureParent : undefined,
  };
}
